import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, switchMap } from 'rxjs';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { JobRequestsService } from '../../../../services/job-requests.service';
import { DesignService } from '../../../../services/design.service';
import { SystemSettingsService } from '../../../../services/system-settings.service';
import { EditHphtItemDto } from '@models/edit-job-request-dto.interface';
import { MudPurposeLookupDto } from '@models/mud-purpose-lookup.interface';
import { SaveHphtItemDto, SaveJobRequestDto } from '@models/save-job-request-dto.interface';

export interface PilotTestDialogData {
  jobId: number;
  jobRequestId?: number;
  requestType?: 'pilot' | 'cutback';
}

type DensityStrategy = 'change' | 'maintain';

@Component({
  selector: 'app-pilot-test-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './pilot-test-dialog.component.html',
  styleUrl: './pilot-test-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PilotTestDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<PilotTestDialogComponent>);
  protected readonly data = inject<PilotTestDialogData>(MAT_DIALOG_DATA);
  private readonly jobRequestsService = inject(JobRequestsService);
  private readonly designService = inject(DesignService);
  private readonly systemSettingsService = inject(SystemSettingsService);
  private readonly fb = inject(FormBuilder);
  private readonly defaultStartDate = this.getTodayDateString();
  private readonly requestType = this.data.requestType ?? 'pilot';

  protected readonly isCreate = this.data.jobRequestId == null;
  protected readonly isCutbackRequest = this.requestType === 'cutback';
  protected isLoading = signal(true);
  protected isSaving = signal(false);
  protected errorMessage = signal('');
  protected isEditable = signal(true);
  protected mudPurposes = signal<MudPurposeLookupDto[]>([]);
  protected totalVolume = signal(0);
  protected readonly densityStrategies: Array<{ value: DensityStrategy; label: string }> = [
    { value: 'change', label: 'Change Density' },
    { value: 'maintain', label: 'Maintain Density' },
  ];

  protected form = this.fb.group({
    densityStrategy: ['change' as DensityStrategy],
    targetDensity: [null as number | null],
    conclusion: [''],
    yieldPointFrom: [null as number | null],
    yieldPointTo: [null as number | null],
    shippingVolume: [null as number | null],
    cementLabVolume: [null as number | null],
    pipeDiameter: [null as number | string | null],
    mudPurposeId: [null as number | null],
    submittedDate: [''],
    neededByDate: [''],
    startDate: [this.defaultStartDate],
    endDate: [''],
    sapCode: [''],
    bariteSgconst: [4.2],
    baseFluidSgconst: [0.79],
    lowGravitySolidsSgconst: [2.6],
    hphtItems: this.fb.array<FormGroup>([]),
  });

  protected focusNextInput(event: KeyboardEvent): void {
    const form = event.currentTarget as HTMLFormElement;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const editableControls = Array.from(form.querySelectorAll<HTMLElement>(
      'input[formControlName]:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([readonly]):not([disabled]):not([tabindex="-1"]), textarea[formControlName]:not([readonly]):not([disabled]):not([tabindex="-1"]), select[formControlName]:not([disabled]):not([tabindex="-1"])'
    ));
    const currentIndex = editableControls.indexOf(target);
    if (currentIndex < 0) return;

    event.preventDefault();
    if (currentIndex < editableControls.length - 1) {
      editableControls[currentIndex + 1].focus();
    }
  }

  ngOnInit(): void {
    if (this.isCutbackRequest) {
      this.form.get('densityStrategy')?.addValidators(Validators.required);
      this.form.get('densityStrategy')?.updateValueAndValidity({ emitEvent: false });
      this.form.get('densityStrategy')?.valueChanges.subscribe(value => {
        this.applyDensityStrategyRules(value as DensityStrategy | null);
      });
    }

    this.form.get('shippingVolume')!.valueChanges.subscribe(() => this.updateTotalVolume());
    this.form.get('cementLabVolume')!.valueChanges.subscribe(() => this.updateTotalVolume());

    // Load WBM fluid constants from system settings as defaults
    this.systemSettingsService.getFluidConstants('WBM').subscribe({
      next: (constants) => {
        this.form.patchValue({
          bariteSgconst: constants.bariteSG,
          baseFluidSgconst: constants.baseFluidSG,
          lowGravitySolidsSgconst: constants.lowGravitySolidsSG,
        }, { emitEvent: false });
      },
      error: () => {
        // Fallback to hardcoded defaults if system settings don't have the values
        this.form.patchValue({
          bariteSgconst: 4.2,
          baseFluidSgconst: 1.0,
          lowGravitySolidsSgconst: 2.6,
        }, { emitEvent: false });
      }
    });

    const request$ = this.isCreate
      ? this.jobRequestsService.getForCreate(this.data.jobId)
      : this.jobRequestsService.getForEdit(this.data.jobRequestId!);

    request$.subscribe({
      next: (dto) => {
        this.mudPurposes.set(dto.mudPurposes);
        this.isEditable.set(dto.isEditable);

        this.form.patchValue({
          densityStrategy: this.isCutbackRequest && dto.densityStrategy ? dto.densityStrategy : 'change',
          targetDensity: dto.targetDensity,
          conclusion: dto.conclusion ?? '',
          yieldPointFrom: dto.yieldPointFrom,
          yieldPointTo: dto.yieldPointTo,
          shippingVolume: dto.shippingVolume,
          cementLabVolume: dto.cementLabVolume,
          pipeDiameter: dto.pipeDiameter,
          mudPurposeId: dto.mudPurposeId,
          submittedDate: dto.submittedDate ? dto.submittedDate.substring(0, 10) : '',
          neededByDate: dto.neededByDate ? dto.neededByDate.substring(0, 10) : '',
          startDate: dto.startDate ? dto.startDate.substring(0, 10) : this.defaultStartDate,
          endDate: dto.endDate ? dto.endDate.substring(0, 10) : '',
          sapCode: dto.sapCode ?? '',
          bariteSgconst: this.isCutbackRequest ? null : (dto.bariteSgconst ?? 4.2),
          baseFluidSgconst: this.isCutbackRequest ? null : (dto.baseFluidSgconst ?? 0.79),
          lowGravitySolidsSgconst: this.isCutbackRequest ? null : (dto.lowGravitySolidsSgconst ?? 2.6),
        });

        this.hphtItemsArray.clear();
        dto.hphtItems.forEach(item => this.hphtItemsArray.push(this.buildHphtGroup(item)));

        if (!dto.isEditable) {
          this.form.disable();
        }

        if (this.isCutbackRequest) {
          this.applyDensityStrategyRules(this.form.get('densityStrategy')?.value as DensityStrategy | null);
        }

        this.updateTotalVolume();
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false),
    });
  }

  protected get hphtItemsArray(): FormArray<FormGroup> {
    return this.form.get('hphtItems') as FormArray<FormGroup>;
  }

  protected onAddHphtRow(): void {
    const rows = this.hphtItemsArray;
    const last = rows.length > 0 ? rows.at(rows.length - 1).getRawValue() : null;
    this.hphtItemsArray.push(this.buildHphtGroup(last ? {
      id: 0,
      temperature: last.temperature,
      pressure: last.pressure,
      pipeDiameter: last.pipeDiameter,
      mudPurposeId: last.mudPurposeId,
    } : undefined));
  }

  protected onRemoveHphtRow(index: number): void {
    this.hphtItemsArray.removeAt(index);
  }

  private buildHphtGroup(item?: EditHphtItemDto): FormGroup {
    return this.fb.group({
      id: [item?.id ?? null],
      temperature: [item?.temperature ?? null],
      pressure: [item?.pressure ?? null],
      pipeDiameter: [item?.pipeDiameter ?? null],
      mudPurposeId: [item?.mudPurposeId ?? null],
    });
  }

  private updateTotalVolume(): void {
    const raw = this.form.getRawValue();
    const shipping = Number(raw.shippingVolume) || 0;
    const cement = Number(raw.cementLabVolume) || 0;
    this.totalVolume.set(shipping + cement);
  }

  protected onSave(): void {
    if (this.form.invalid) return;

    const raw = this.form.getRawValue();
    const pipeDiameter = this.parsePipeDiameter(raw.pipeDiameter);
    if (Number.isNaN(pipeDiameter)) {
      this.errorMessage.set('Pipe diameter must be a number or a fraction, such as 9 7/8".');
      return;
    }

    const dto: SaveJobRequestDto = {
      jobId: this.data.jobId,
      densityStrategy: this.isCutbackRequest ? (raw.densityStrategy as DensityStrategy | null) : undefined,
      targetDensity: raw.targetDensity != null ? Number(raw.targetDensity) : undefined,
      conclusion: raw.conclusion || undefined,
      yieldPointFrom: raw.yieldPointFrom != null ? Number(raw.yieldPointFrom) : undefined,
      yieldPointTo: raw.yieldPointTo != null ? Number(raw.yieldPointTo) : undefined,
      shippingVolume: raw.shippingVolume != null ? Number(raw.shippingVolume) : undefined,
      cementLabVolume: raw.cementLabVolume != null ? Number(raw.cementLabVolume) : undefined,
      pipeDiameter,
      mudPurposeId: raw.mudPurposeId != null ? Number(raw.mudPurposeId) : undefined,
      submittedDate: raw.submittedDate ? new Date(raw.submittedDate).toISOString() : undefined,
      neededByDate: raw.neededByDate ? new Date(raw.neededByDate).toISOString() : undefined,
      startDate: raw.startDate ? new Date(raw.startDate).toISOString() : undefined,
      endDate: raw.endDate ? new Date(raw.endDate).toISOString() : undefined,
      sapCode: raw.sapCode || undefined,
      bariteSgconst: !this.isCutbackRequest && raw.bariteSgconst != null ? Number(raw.bariteSgconst) : undefined,
      baseFluidSgconst: !this.isCutbackRequest && raw.baseFluidSgconst != null ? Number(raw.baseFluidSgconst) : undefined,
      lowGravitySolidsSgconst: !this.isCutbackRequest && raw.lowGravitySolidsSgconst != null ? Number(raw.lowGravitySolidsSgconst) : undefined,
      hphtItems: raw.hphtItems.map((item): SaveHphtItemDto => ({
        id: item['id'] || undefined,
        temperature: item['temperature'] != null ? Number(item['temperature']) : undefined,
        pressure: item['pressure'] != null ? Number(item['pressure']) : undefined,
        pipeDiameter: item['pipeDiameter'] != null ? Number(item['pipeDiameter']) : undefined,
        mudPurposeId: item['mudPurposeId'] != null ? Number(item['mudPurposeId']) : undefined,
      })),
    };

    this.isSaving.set(true);
    this.errorMessage.set('');

    const save$: Observable<unknown> = this.isCreate
      ? (this.isCutbackRequest
          ? this.jobRequestsService.createCutbackWeighUp(dto).pipe(
              switchMap(jobRequestId => this.designService.ensureFirstIteration(jobRequestId))
            )
          : this.jobRequestsService.createPilotTest(dto))
      : this.jobRequestsService.updateJobRequest(this.data.jobRequestId!, dto);

    if (this.isCutbackRequest && !raw.densityStrategy) {
      this.errorMessage.set('Density strategy is required for Cutback/Weigh Up requests.');
      this.isSaving.set(false);
      return;
    }

    if (this.isCutbackRequest && raw.densityStrategy === 'change' && dto.targetDensity == null) {
      this.errorMessage.set('Target density is required for Cutback/Weigh Up requests when strategy is Change.');
      this.isSaving.set(false);
      return;
    }

    save$.subscribe({
      next: () => {
        this.isSaving.set(false);
        this.dialogRef.close(true);
      },
      error: (err: HttpErrorResponse) => {
        this.isSaving.set(false);
        this.errorMessage.set(this.extractErrorMessage(err));
      },
    });
  }

  private parsePipeDiameter(value: number | string | null): number | undefined {
    if (value == null || String(value).trim() === '') return undefined;
    if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;

    const normalized = value.replace(/["']/g, '').trim();
    const mixedFraction = normalized.match(/^(\d+)\s+(\d+)\s*\/\s*(\d+)$/);
    if (mixedFraction) {
      const denominator = Number(mixedFraction[3]);
      return denominator === 0
        ? NaN
        : Number(mixedFraction[1]) + Number(mixedFraction[2]) / denominator;
    }

    const fraction = normalized.match(/^(\d+)\s*\/\s*(\d+)$/);
    if (fraction) {
      const denominator = Number(fraction[2]);
      return denominator === 0 ? NaN : Number(fraction[1]) / denominator;
    }

    const numericValue = Number(normalized);
    return Number.isFinite(numericValue) ? numericValue : NaN;
  }

  private extractErrorMessage(err: HttpErrorResponse): string {
    if (err.status === 0) {
      return 'Unable to reach API. Please ensure the backend is running and reachable.';
    }

    const body = err.error;
    const errors = body?.errors as Record<string, unknown> | undefined;
    if (errors) {
      const firstKey = Object.keys(errors)[0];
      if (firstKey) {
        const firstValue = errors[firstKey];
        if (Array.isArray(firstValue) && firstValue.length > 0) {
          return String(firstValue[0]);
        }
        if (typeof firstValue === 'string' && firstValue.trim().length > 0) {
          return firstValue;
        }
      }
    }

    if (typeof body === 'string' && body.trim().length > 0) {
      return body;
    }

    if (typeof body?.detail === 'string' && body.detail.trim().length > 0) {
      return body.detail;
    }

    if (typeof body?.title === 'string' && body.title.trim().length > 0) {
      return body.title;
    }

    if (typeof body?.message === 'string' && body.message.trim().length > 0) {
      return body.message;
    }

    if (typeof err.message === 'string' && err.message.trim().length > 0) {
      return err.message;
    }

    return 'Save failed. Please check your entries and try again.';
  }

  protected onCancel(): void {
    this.dialogRef.close(false);
  }

  private applyDensityStrategyRules(strategy: DensityStrategy | null): void {
    if (!this.isCutbackRequest) return;

    const targetDensityControl = this.form.get('targetDensity');
    if (!targetDensityControl) return;

    if (strategy === 'change') {
      targetDensityControl.addValidators(Validators.required);
    } else {
      targetDensityControl.removeValidators(Validators.required);
    }

    targetDensityControl.updateValueAndValidity({ emitEvent: false });
  }

  private getTodayDateString(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
